import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Radio } from 'antd';
import type { CSSProperties } from 'react';
import { CustomModal } from 'components/molecules/PricingSimulator/Modals/ScenarioColumnEditModal';
import useEventAttributeFilters from '../../../EventsPage/hooks/useEventAttributeFilters';
import type { UseDimOptionsHook } from '../../../../components/DimSelect';
import type { Labels } from '../../../../config/labels';
import type { OptimizePlanConfig } from '../../../../config/optimizePlanConfig';
import {
    TPO_OPTIMIZE_CONSTRAINTS_PANEL,
    TPO_OPTIMIZE_MODAL,
    TPO_OPTIMIZE_SCOPE_PANEL,
    TPO_OPTIMIZE_TAB_CONSTRAINTS,
    TPO_OPTIMIZE_TAB_SCOPE,
    TPO_OPTIMIZE_TABS,
} from '../../../../config/testIds';
import { useCustomerConfig, useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import { usePlanDetailsContext } from '../../../context/PlanDetailsContext';
import type { RunOptimizationResponse } from '../../hooks/mutations/useRunOptimizationMutation';

import { useObjectiveOptions } from '../../hooks/queries/useObjectiveOptions';
import usePromotionCount from '../../hooks/queries/usePromotionCount';
import { useConstraintOriginalValues } from '../../hooks/queries/useConstraintOriginalValues';
import useRunOptimizationMutation from '../../hooks/mutations/useRunOptimizationMutation';
import { useOptimizeConstraints } from '../../hooks/ui/useOptimizeConstraints';
import { useOptimizePlanForm } from '../../hooks/ui/useOptimizePlanForm';
import { buildOptimizationPayload } from '../../utils/optimizePayloadBuilder';
import { areConstraintGroupsSubmittable, buildConstraintGroups } from '../../utils/buildConstraintGroups';
import { queryClient } from 'services/reactQueryService';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { TPOGEN_OPTIMIZE_STARTED } from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../../../utils/trackTpoGenEvent';

import ScopeTab from './ScopeTab';
import ConstraintsTab from './ConstraintsTab';
import OptimizePlanFooter from './OptimizePlanFooter';

/**
 * Adapts `useEventAttributeFilters` to the `UseDimOptionsHook` interface expected
 * by `ConfigDrivenFormFields` / `DimSelect`.  Mirrors the `useEventDimOptions`
 * pattern in EventsSection.tsx — defined at module level so the reference is
 * stable and never triggers unnecessary re-renders.
 */
const useOptimizeDimOptions: UseDimOptionsHook = (params) =>
    useEventAttributeFilters({
        dimension: params.dimension,
        attributeType: params.attributeType,
        lowestAttribute: params.lowestAttribute ?? false,
        filters: (params.filters ?? {}) as Record<string, unknown>,
        selectedDimension: params.selectedDimension ?? {
            value: '',
            dimension: params.dimension,
            attributeType: params.attributeType,
        },
        searchString: params.searchString ?? '',
        enabled: params.enabled,
    });

type ActiveTab = 'scope' | 'constraints';

type StatusMessageLabels = Labels['optimizePlan']['statusMessages'];

/** Context carried alongside the job id so the caller can attach it to the completed/failed analytics events. */
export interface OptimizationSubmitContext {
    objective: string | null;
    constraintCount: number;
}

interface Props {
    open: boolean;
    onCancel: () => void;
    onSubmitFinish: (data: RunOptimizationResponse, context: OptimizationSubmitContext) => void;
    planId: string;
    config: OptimizePlanConfig;
}

const MODAL_WIDTH = 1080;

const MODAL_STYLE: CSSProperties = { borderRadius: 8 };
const MODAL_BODY_STYLE: CSSProperties = { height: 600, overflow: 'hidden' };

/**
 * In-flight work is reported even though it also blocks the run: being mid-fetch is
 * *why* the run is blocked, so saying so is more useful than staying silent. Only a
 * validation block — an incomplete constraint the user can see — goes unexplained.
 */
const resolveStatusMessage = (
    labels: StatusMessageLabels,
    state: { submitting: boolean; constraintsLoading: boolean; promotionCountLoading: boolean },
): string | undefined => {
    if (state.submitting) return labels.startingOptimization;
    if (state.constraintsLoading) return labels.loadingConstraints;
    if (state.promotionCountLoading) return labels.countingPromotions;
    return undefined;
};

const OptimizePlanModal: React.FC<Props> = ({ open, onCancel, onSubmitFinish, planId, config }) => {
    const tpoGenConfig = useTpoGenConfig();
    const { metricFormatByKey } = useCustomerConfig();
    const { currentPlanDetails } = usePlanDetailsContext();
    const planYear = currentPlanDetails?.plan_year;
    const [activeTab, setActiveTab] = useState<ActiveTab>('scope');
    const [form] = Form.useForm();

    // --- UI hook (pure client state + derivations) ---
    const {
        formData,
        setFormData,
        levelOptions,
        scopeSelections,
        scopeFilters,
        promotionCountStartDate,
        constraintValuesContext,
        resetForm,
    } = useOptimizePlanForm({ form, planId, config });

    // --- Queries ---
    const { objectiveList, objectiveListLoading, error: objectiveListQueryError } = useObjectiveOptions(open);
    const objectiveListError = objectiveListQueryError != null;

    const { promotionTotalCount, promotionCountLoading } = usePromotionCount({
        planId,
        filters: scopeFilters,
        startDate: promotionCountStartDate,
        enabled: open,
    });

    // --- Mutation ---
    const { mutate: runOptimizationMutate, isPending: runOptimizationLoading } = useRunOptimizationMutation();

    // One request covers every constraint at the scoped level and all levels below,
    // so it runs off the scope alone and carries the catalog the selector offers.
    // Adding a level afterwards is pure UI — the numbers are already here.
    const {
        catalog,
        byLevel,
        status: constraintValuesStatus,
    } = useConstraintOriginalValues({ context: constraintValuesContext, metricFormatByKey, enabled: open });

    const constraintsLoading = constraintValuesStatus === 'loading';
    const constraintsError = constraintValuesStatus === 'error';

    // --- UI hook (constraint selection state) ---
    const {
        selections,
        inputs,
        kpiOptions,
        selectedConstraintNames,
        onKPISelectHandler,
        onAddLevel,
        onRemoveLevel,
        onRemoveAttribute,
        onInputChange,
        resetConstraints,
    } = useOptimizeConstraints({
        catalog,
        byLevel,
        valuesContextSignature: JSON.stringify(constraintValuesContext),
    });

    const constraintGroups = useMemo(
        () =>
            buildConstraintGroups({
                selections,
                config,
                catalog,
                levelOptions,
                byLevel,
                valuesStatus: constraintValuesStatus,
                inputs,
            }),
        [selections, config, catalog, levelOptions, byLevel, constraintValuesStatus, inputs],
    );

    // --- Effects (transferred verbatim from useOptimizePlan) ---

    // Reset keep_top whenever the available promotion pool changes.
    const prevPromotionTotalCountRef = useRef(promotionTotalCount);
    useEffect(() => {
        if (prevPromotionTotalCountRef.current !== promotionTotalCount) {
            prevPromotionTotalCountRef.current = promotionTotalCount;
            setFormData((prev) => ({ ...prev, keep_top: null }));
        }
    }, [promotionTotalCount, setFormData]);

    // Dropped on CLOSE, not on open. Baselines are only meaningful for the run being
    // configured, but the fetch now starts as soon as the objective and date are set
    // — so removing on open would cancel the very query that just began and strand
    // the tab in a permanent loading state.
    useEffect(() => {
        if (!open) {
            queryClient.removeQueries({ queryKey: ['tpo-gen', 'promotion-count'] });
            queryClient.removeQueries({
                exact: false,
                queryKey: [...tpoGenKeys.all, 'constraint-original-values'],
            });
        }
    }, [open]);

    // --- Composed resets & handlers ---
    const resetAll = useCallback(() => {
        resetForm();
        resetConstraints();
    }, [resetForm, resetConstraints]);

    const handleCancel = useCallback(() => {
        resetAll();
        onCancel();
    }, [resetAll, onCancel]);

    const handleModalCancel = useCallback(() => {
        handleCancel();
        setActiveTab('scope');
    }, [handleCancel]);

    const handleSubmit = useCallback(() => {
        const apiPayload = buildOptimizationPayload({
            planId,
            formData,
            constraintGroups,
            scopeSelections,
            baseInputFields: config.baseInputFields,
            limitConstraintFields: config.limitConstraintFields,
        });

        trackTpoGenEvent(TPOGEN_OPTIMIZE_STARTED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, {
            plan_id: planId,
            objective: formData.objective,
            constraint_count: constraintGroups.length,
        });

        runOptimizationMutate(apiPayload, {
            onSuccess: (data: RunOptimizationResponse) => {
                resetAll();
                onSubmitFinish(data, { objective: formData.objective, constraintCount: constraintGroups.length });
            },
        });
    }, [
        planId,
        formData,
        constraintGroups,
        scopeSelections,
        config.baseInputFields,
        config.limitConstraintFields,
        runOptimizationMutate,
        resetAll,
        onSubmitFinish,
    ]);

    const hasMissingRequiredFields = config.baseInputFields.some(
        (f) => f.required && f.name != null && formData[f.name] == null,
    );

    // v3 tightens the gate: every KPI needs a level, every level needs a row, and
    // every row needs a complete, non-inverted input before anything is submitted.
    const isSubmitDisabled =
        constraintsError || hasMissingRequiredFields || !areConstraintGroupsSubmittable(constraintGroups);

    const statusMessage = resolveStatusMessage(tpoGenConfig.labels.optimizePlan.statusMessages, {
        submitting: runOptimizationLoading,
        constraintsLoading,
        promotionCountLoading,
    });
    const isRunBlocked = isSubmitDisabled || statusMessage != null;

    const modalTitle = (
        <div className="flex items-center gap-4">
            <span className="text-base font-medium text-[#18181b]">{tpoGenConfig.labels.optimizePlan.modalTitle}</span>
            <Radio.Group
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="flex"
                data-testid={TPO_OPTIMIZE_TABS}
            >
                <Radio.Button
                    value="scope"
                    className="!rounded-l-md !rounded-r-none"
                    data-testid={TPO_OPTIMIZE_TAB_SCOPE}
                    data-state-active={activeTab === 'scope' ? 'true' : undefined}
                >
                    {tpoGenConfig.labels.optimizePlan.tabs.scope}
                </Radio.Button>
                <Radio.Button
                    value="constraints"
                    className="!rounded-r-md !rounded-l-none"
                    data-testid={TPO_OPTIMIZE_TAB_CONSTRAINTS}
                    data-state-active={activeTab === 'constraints' ? 'true' : undefined}
                >
                    {tpoGenConfig.labels.optimizePlan.tabs.constraints}
                </Radio.Button>
            </Radio.Group>
        </div>
    );

    return (
        <CustomModal
            style={MODAL_STYLE}
            bodyStyle={MODAL_BODY_STYLE}
            width={MODAL_WIDTH}
            footer={
                <OptimizePlanFooter
                    statusMessage={statusMessage}
                    cancelLabel={tpoGenConfig.labels.common.cancel}
                    runLabel={tpoGenConfig.labels.optimizePlan.runOptimisation}
                    runDisabled={isRunBlocked}
                    onCancel={handleModalCancel}
                    onRun={handleSubmit}
                />
            }
            maskClosable={false}
            destroyOnClose
            centered
            open={open}
            onCancel={handleModalCancel}
            title={modalTitle}
        >
            <Form form={form} layout="vertical" style={{ height: '100%' }} data-testid={TPO_OPTIMIZE_MODAL}>
                <div className="h-full flex flex-col overflow-hidden">
                    <div
                        className={activeTab === 'scope' ? 'h-full' : 'hidden'}
                        data-testid={TPO_OPTIMIZE_SCOPE_PANEL}
                        data-state-active={activeTab === 'scope' ? 'true' : undefined}
                    >
                        <ScopeTab
                            scopeOfOptimisationFields={config.scopeOfOptimisationFields}
                            baseInputFields={config.baseInputFields}
                            useDimOptions={useOptimizeDimOptions}
                            formData={formData}
                            setFormData={setFormData}
                            objectiveList={objectiveList}
                            objectiveListLoading={objectiveListLoading}
                            objectiveListError={objectiveListError}
                            promotionTotalCount={promotionTotalCount}
                            promotionCountLoading={promotionCountLoading}
                            planYear={planYear}
                        />
                    </div>
                    <div
                        className={activeTab === 'constraints' ? 'h-full' : 'hidden'}
                        data-testid={TPO_OPTIMIZE_CONSTRAINTS_PANEL}
                        data-state-active={activeTab === 'constraints' ? 'true' : undefined}
                    >
                        <ConstraintsTab
                            groups={constraintGroups}
                            tableHeaders={config.constraintTableHeaders}
                            kpiList={kpiOptions}
                            selectedConstraintNames={selectedConstraintNames}
                            onKPISelectHandler={onKPISelectHandler}
                            onAddLevel={onAddLevel}
                            onRemoveLevel={onRemoveLevel}
                            onRemoveAttribute={onRemoveAttribute}
                            onInputChange={onInputChange}
                            errorExists={constraintsError}
                            isLoading={constraintsLoading}
                            selectorDisabled={constraintValuesContext == null}
                        />
                    </div>
                </div>
            </Form>
        </CustomModal>
    );
};

OptimizePlanModal.displayName = 'OptimizePlanModal';
export default OptimizePlanModal;
